import { FullscreenExitOutlined } from "@ant-design/icons";
import { Layout } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router";
import { Fab } from "react-tiny-fab";
import "react-tiny-fab/dist/styles.css";
import { sizePad } from "../../services/common/constants";
import { UpdateAppConfig } from "../../services/redux/reducers/app";
import MyHeader from "./MyHeader";
import "./MyLayout.css";
import MySidebar from "./MySidebar";

import MyFooter from "./MyFooter";
import FloatingQRCode from "./FloatingQRCode";

function MyLayout(props) {
  const dispatch = useDispatch();
  const authData = useSelector((x) => x.authService);
  const appData = useSelector((x) => x.app);
  const cncService = useSelector((x) => x.cncService);
  const cwService = useSelector((x) => x.cwService);
  const { screenSize } = appData;

  const { username, security_level, fullname, avatar, group_ids } = authData;
  const [autoScrollSpeed, setAutoScrollSpeed] = useState(50);

  const [isFullScreen, setIsFullScreen] = useState(false);

  const [isWaitingToTop, setIsWaitingToTop] = useState(false);
  const timerRefs = useRef({ timeout1: null, timeout2: null, rafId: null });
  const scrollStateRef = useRef({ velocity: 0, lastTime: null });
  const autoScrollRef = useRef(false);
  const NEAR_BOTTOM_THRESHOLD = 8;
  const SCROLL_SPEED_FACTOR = 0.5; // reduce scroll speed (1 = original, 0.5 = half speed)

  // sync with redux
  const customerInfoList = cncService.customerInfoList;
  const customerIdList = Object.keys(customerInfoList);
  const customDashboardList = cwService.cwDashboards;

  const isMenuCollapsed = appData.is_menu_collapsed;
  const selCustomerId = appData.customer_id;
  const selMenuType = appData.y;
  const customerInfo = customerInfoList[selCustomerId];

  const viewMode = appData.viewMode;
  const autoScroll = appData.isAutoScroll;
  autoScrollRef.current = autoScroll;

  const setIsMenuCollapsed = (val) => {
    UpdateAppConfig({ is_menu_collapsed: val }, dispatch);
  };

  const setSelMenuType = (val) => {
    UpdateAppConfig({ page_type: val }, dispatch);
  };

  const setViewMode = (val) => {
    UpdateAppConfig({ viewMode: val }, dispatch);
  };

  const setAutoScroll = (val) => {
    UpdateAppConfig({ isAutoScroll: val }, dispatch);
  };

  const myRef = useRef(null);

  const startScrollLoop = () => {
    const el = myRef.current;
    if (!el || autoScrollRef.current !== true) return;
    const { velocity } = scrollStateRef.current;
    if (velocity <= 0) return;

    const loop = (timestamp) => {
      const refs = timerRefs.current;
      if (refs.rafId === null) return; // was cancelled
      const el = myRef.current;
      if (!el) return;

      const state = scrollStateRef.current;
      const lastTime = state.lastTime ?? timestamp;
      const deltaMs = Math.min(timestamp - lastTime, 100);
      state.lastTime = timestamp;

      const maxScroll = el.scrollHeight - el.offsetHeight;
      const threshold = NEAR_BOTTOM_THRESHOLD;
      const nearBottom = el.scrollTop >= maxScroll - threshold;

      if (nearBottom && maxScroll > 0) {
        refs.rafId = null;
        state.lastTime = null;
        el.scrollTop = el.scrollHeight;
        setIsWaitingToTop(true);
        if (refs.timeout1) clearTimeout(refs.timeout1);
        if (refs.timeout2) clearTimeout(refs.timeout2);
        refs.timeout1 = setTimeout(() => {
          if (!myRef.current) return;
          myRef.current.scrollTo({ behavior: "smooth", top: 0 });
          refs.timeout2 = setTimeout(() => {
            scrollStateRef.current.lastTime = null;
            refs.rafId = requestAnimationFrame(loop);
            setIsWaitingToTop(false);
          }, 1000);
        }, 500);
        return;
      }

      const deltaSec = deltaMs / 1000;
      const nextTop = Math.min(el.scrollTop + state.velocity * deltaSec, maxScroll);
      el.scrollTop = nextTop;
      refs.rafId = requestAnimationFrame(loop);
    };

    timerRefs.current.rafId = requestAnimationFrame(loop);
  };

  const stopScrollLoop = () => {
    const refs = timerRefs.current;
    if (refs.rafId != null) {
      cancelAnimationFrame(refs.rafId);
      refs.rafId = null;
    }
    scrollStateRef.current.lastTime = null;
  };

  // On unmount: clear scroll-to-top timers and rAF.
  useEffect(() => {
    return () => {
      const refs = timerRefs.current;
      if (refs.timeout1) {
        clearTimeout(refs.timeout1);
        refs.timeout1 = null;
      }
      if (refs.timeout2) {
        clearTimeout(refs.timeout2);
        refs.timeout2 = null;
      }
      stopScrollLoop();
    };
  }, []);

  // Auto-scroll: when on, compute velocity (px/s) and start rAF loop for smooth scrolling.
  useEffect(() => {
    if (!myRef.current) return;
    myRef.current.scrollTop = 0;
    stopScrollLoop();

    if (autoScroll === true) {
      const height = myRef.current.scrollHeight - myRef.current.offsetHeight;
      const durationSec = Math.max(1, 31 - autoScrollSpeed * 0.3);
      scrollStateRef.current.velocity = (height / durationSec) * SCROLL_SPEED_FACTOR;
      startScrollLoop();
    }

    return () => stopScrollLoop();
  }, [autoScroll, autoScrollSpeed]);

  return (
    <Layout>
      {!isFullScreen && (
        <MySidebar
          isMenuCollapsed={isMenuCollapsed}
          setIsMenuCollapsed={setIsMenuCollapsed}
          selCustomerId={selCustomerId}
          customerInfo={customerInfo}
          customerIdList={customerIdList}
          customDashboardList={customDashboardList}
          customerInfoList={customerInfoList}
          setAutoScroll={setAutoScroll}
          screenSize={screenSize}
          security_level={security_level}
          group_ids={group_ids}
        />
      )}

      <Layout>
        {!isFullScreen && (
          <MyHeader
            customerInfo={customerInfo}
            viewMode={viewMode}
            setViewMode={setViewMode}
            setIsFullScreen={setIsFullScreen}
            isMenuCollapsed={isMenuCollapsed}
            setIsMenuCollapsed={setIsMenuCollapsed}
            selCustomerId={selCustomerId}
            setAutoScroll={setAutoScroll}
            screenSize={screenSize}
            security_level={security_level}
            avatar={avatar}
            fullname={fullname}
            setSelMenuType={setSelMenuType}
            username={username}
            selMenuType={selMenuType}
          />
        )}
        {screenSize.width >= sizePad ? (
          <div
            className="content-scrollbar-style"
            ref={myRef}
            style={{
              height: !isFullScreen
                ? window.innerHeight - 140
                : window.innerHeight - 50,
              overflow: "auto",
              overflowX: "hidden",
              margin: "10px 16px",
            }}
          >
            {props.children}
          </div>
        ) : (
          <div
            className="content-scrollbar-style"
            ref={myRef}
            style={{
              height: !isFullScreen
                ? window.innerHeight - 180
                : window.innerHeight - 50,
              overflow: "auto",
              overflowX: "hidden",
              margin: "10px 16px",
            }}
          >
            {props.children}
          </div>
        )}

        <MyFooter
          screenSize={screenSize}
          customerInfo={customerInfo}
          isMenuCollapsed={isMenuCollapsed}
          isFullScreen={isFullScreen}
          autoScroll={autoScroll}
          setAutoScroll={setAutoScroll}
          autoScrollSpeed={autoScrollSpeed}
          setAutoScrollSpeed={setAutoScrollSpeed}
          isWaitingToTop={isWaitingToTop}
        />
      </Layout>

      {isFullScreen && (
        <Fab
          position={{ bottom: 24, right: 24 }}
          icon={<FullscreenExitOutlined />}
          onClick={() => setIsFullScreen(false)}
        ></Fab>
      )}

      {/* Floating QR Code - Only visible for user 'cam' */}
      {!isFullScreen && <FloatingQRCode username={username} />}
    </Layout>
  );
}

export default withRouter(MyLayout);
